import React, { useCallback, useContext, useMemo, useState } from 'react';

import DatasetDefinitionsContext from 'contexts/DatasetDefinitionsContext';
import FeatureGatesContext from 'contexts/FeatureGatesContext';
import PerformanceBoardSlideOutContext from 'components/PerformanceBoards/contexts/PerformanceBoardSlideOutContext';
import SelectedEntityContext from 'components/PerformanceBoards/contexts/SelectedEntityContext';
import DragAndDropListContext from 'components/DragAndDropList/DragAndDropListContext';
import useShouldAggregateSignOff from 'components/PerformanceBoards/hooks/useShouldAggregateSignOff';
import DisplaySection from './DisplaySection';
import AnalyticsContext from 'contexts/AnalyticsContext';
import SectionHeader from './SectionHeader';
import sectionTypeCheckers from '../sectionTypeCheckers';

const SectionListItem = ({
  sectionId,
  section,
  removeSection,
  noPadding,
}: {
  sectionId: string;
  section: PerformanceBoardTypes.SlideOut.SlideOutSection | undefined;
  removeSection: (sectionId: string) => void;
  noPadding?: boolean;
}) => {
  const { setNavState, board } = useContext(PerformanceBoardSlideOutContext);
  const { isDragging } = useContext(DragAndDropListContext);
  const { trackEvent } = useContext(AnalyticsContext);
  const { bonusPrograms } = useContext(DatasetDefinitionsContext);
  const { selectedEntityDocument } = useContext(SelectedEntityContext);
  const { isAdditionalPaymentsEnabled } = useContext(FeatureGatesContext);
  const [isHovering, setIsHovering] = useState<boolean>(false);
  const shouldAggregateSignOff = useShouldAggregateSignOff(board);

  const isHiddenBonusReward = useMemo(() => {
    if (!section || !sectionTypeCheckers.isBonusRewardSection(section)) {
      return false;
    }
    // Aggregated sign-off rows only carry the board's visible columns, so a
    // missing pay field there doesn't mean the reward doesn't apply.
    if (shouldAggregateSignOff) {
      return false;
    }
    const bonusProgram = bonusPrograms.find(
      (bp) => bp.id === section.bonusConfigId,
    );
    const payField = bonusProgram?.rewardFields.find(
      (rf) => rf.name === section.rewardName,
    )?.rewardValueField;
    if (!payField || !selectedEntityDocument) {
      return false;
    }
    return !(payField in selectedEntityDocument);
  }, [bonusPrograms, section, selectedEntityDocument, shouldAggregateSignOff]);

  const onRemoveClicked = useCallback(() => {
    removeSection(sectionId);
    trackEvent('Board - Slide out - Widget Deleted');
  }, [removeSection, sectionId, trackEvent]);

  const onEditClicked = useCallback(() => {
    if (!section) {
      return null;
    }

    setNavState({
      slideoutMode: 'Creating',
      creatingSectionType: section.sectionType,
      editingSection: section,
    });

    trackEvent('Board - Slide out - Edit Widget Clicked');
  }, [section, setNavState, trackEvent]);

  // An Additional Payments section stays on the board but is hidden until the
  // account's additionalPayments gate is opened.
  const isHiddenAdditionalPayments =
    !!section &&
    sectionTypeCheckers.isRecognitionPaymentsSection(section) &&
    !isAdditionalPaymentsEnabled;

  if (!section || isHiddenBonusReward || isHiddenAdditionalPayments) {
    return null;
  }

  return (
    <div
      style={{
        marginBottom: '8px',
        paddingBottom: '8px',
        paddingTop: '8px',
        backgroundColor:
          isHovering && !isDragging ? 'var(--color-bg-hover)' : undefined,
        borderRadius: '4px',
      }}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <SectionHeader
        title={section.title}
        onRemoveClicked={onRemoveClicked}
        onEditClicked={onEditClicked}
        isHovering={isHovering}
        section={section}
      />
      {!isDragging && (
        <div
          style={{
            padding: noPadding ? 0 : '0 30px',
          }}
        >
          <DisplaySection section={section} />
        </div>
      )}
    </div>
  );
};

export default SectionListItem;
